export const roboticsTopics = [
  {
    "id": "robot-kinematics",
    "title": "Robot kinematics",
    "group": "Foundations of robotics",
    "definition": "How robot joints, links, wheels, and bodies translate commands into position, orientation, velocity, and reachable workspace.",
    "coreIdeas": [
      "forward kinematics",
      "inverse kinematics",
      "workspace",
      "Jacobian",
      "singularities"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "humanoid-robots",
      "mobile-manipulators"
    ]
  },
  {
    "id": "robot-dynamics",
    "title": "Robot dynamics",
    "group": "Foundations of robotics",
    "definition": "How mass, inertia, friction, payload, gravity, and contact forces affect real robot motion.",
    "coreIdeas": [
      "rigid body dynamics",
      "torque",
      "friction",
      "payload",
      "contact"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "humanoid-robots",
      "robot-dogs-quadrupeds"
    ]
  },
  {
    "id": "control-theory",
    "title": "Control theory",
    "group": "Foundations of robotics",
    "definition": "Feedback loops that keep motors, joints, arms, drones, legs, and vehicles stable and accurate.",
    "coreIdeas": [
      "PID",
      "model predictive control",
      "impedance control",
      "force control",
      "feedback"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "drones-aerial-robots",
      "robot-dogs-quadrupeds"
    ]
  },
  {
    "id": "mechatronics",
    "title": "Mechatronics",
    "group": "Foundations of robotics",
    "definition": "Integration of mechanical design, electronics, motors, sensors, power, embedded software, and safety hardware.",
    "coreIdeas": [
      "motors",
      "mechanisms",
      "microcontrollers",
      "power electronics",
      "cabling"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "mobile-manipulators",
      "humanoid-robots"
    ]
  },
  {
    "id": "robot-safety",
    "title": "Robot safety",
    "group": "Foundations of robotics",
    "definition": "Engineering and operational discipline that prevents robots from harming people, assets, data, and workflows.",
    "coreIdeas": [
      "safe stop",
      "risk assessment",
      "protected zones",
      "human override",
      "validation"
    ],
    "relatedRobotTypes": [
      "collaborative-robots-cobots",
      "warehouse-robots-amrs",
      "industrial-robots"
    ]
  },
  {
    "id": "human-robot-interaction",
    "title": "Human robot interaction",
    "group": "Foundations of robotics",
    "definition": "How humans understand, supervise, teach, interrupt, collaborate with, and trust robots.",
    "coreIdeas": [
      "interfaces",
      "trust",
      "operator workload",
      "voice",
      "gesture"
    ],
    "relatedRobotTypes": [
      "service-robots",
      "domestic-personal-robots",
      "cleaning-robots",
      "restaurant-robots",
      "retail-robots"
    ]
  },
  {
    "id": "computer-vision-for-robots",
    "title": "Computer vision for robots",
    "group": "Perception and sensing",
    "definition": "How robots use images to detect objects, segment scenes, read labels, inspect surfaces, and guide manipulation.",
    "coreIdeas": [
      "object detection",
      "segmentation",
      "pose estimation",
      "visual servoing",
      "inspection"
    ],
    "relatedRobotTypes": [
      "quality-inspection-robots",
      "warehouse-picking-robots",
      "wheeled-mobile-robots"
    ]
  },
  {
    "id": "3d-perception",
    "title": "3D perception",
    "group": "Perception and sensing",
    "definition": "How robots understand geometry through point clouds, depth cameras, stereo vision, LiDAR, and reconstruction.",
    "coreIdeas": [
      "point clouds",
      "depth",
      "registration",
      "3D reconstruction",
      "grasp pose"
    ],
    "relatedRobotTypes": [
      "warehouse-picking-robots",
      "mobile-manipulators",
      "wheeled-mobile-robots"
    ]
  },
  {
    "id": "sensor-fusion",
    "title": "Sensor fusion",
    "group": "Perception and sensing",
    "definition": "How robots combine cameras, LiDAR, radar, IMU, encoders, force sensors, and maps into one estimate.",
    "coreIdeas": [
      "Kalman filters",
      "factor graphs",
      "uncertainty",
      "time synchronization",
      "calibration"
    ],
    "relatedRobotTypes": [
      "wheeled-mobile-robots",
      "drones-aerial-robots",
      "robot-dogs-quadrupeds"
    ]
  },
  {
    "id": "tactile-sensing",
    "title": "Tactile sensing",
    "group": "Perception and sensing",
    "definition": "How robots use touch and pressure to grasp, slide, feel contact, detect slip, and handle objects.",
    "coreIdeas": [
      "pressure arrays",
      "slip",
      "force",
      "robot skin",
      "dexterity"
    ],
    "relatedRobotTypes": [
      "humanoid-robot-hands",
      "robotic-grippers-and-end-effectors",
      "soft-robotics"
    ]
  },
  {
    "id": "calibration",
    "title": "Calibration",
    "group": "Perception and sensing",
    "definition": "Aligning sensors, robot joints, cameras, tools, and coordinate frames so perception matches physical reality.",
    "coreIdeas": [
      "camera calibration",
      "hand eye calibration",
      "tool center point",
      "extrinsics",
      "repeatability"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "humanoid-robot-hands",
      "wheeled-mobile-robots"
    ]
  },
  {
    "id": "robotics-datasets",
    "title": "Robotics datasets",
    "group": "Perception and sensing",
    "definition": "Recorded sensor, action, language, and task data used to train, evaluate, and debug robot behavior.",
    "coreIdeas": [
      "demonstrations",
      "teleoperation",
      "data engines",
      "benchmarks",
      "annotation"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "slam",
    "title": "SLAM",
    "group": "Autonomy, planning, and AI",
    "definition": "Simultaneous localization and mapping, where a robot builds or updates a map while estimating where it is.",
    "coreIdeas": [
      "LiDAR SLAM",
      "visual SLAM",
      "loop closure",
      "map drift",
      "pose graph"
    ],
    "relatedRobotTypes": [
      "warehouse-robots-amrs",
      "wheeled-mobile-robots",
      "robot-dogs-quadrupeds"
    ]
  },
  {
    "id": "motion-planning",
    "title": "Motion planning",
    "group": "Autonomy, planning, and AI",
    "definition": "Computing collision free robot movements while respecting speed, acceleration, joint, and safety constraints.",
    "coreIdeas": [
      "path planning",
      "trajectory optimization",
      "sampling planners",
      "collision checking",
      "constraints"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "mobile-manipulators",
      "wheeled-mobile-robots"
    ]
  },
  {
    "id": "task-planning",
    "title": "Task planning",
    "group": "Autonomy, planning, and AI",
    "definition": "Choosing the sequence of actions needed to pick, move, inspect, deliver, assemble, or recover from failure.",
    "coreIdeas": [
      "behavior trees",
      "symbolic planning",
      "task graphs",
      "state machines",
      "recovery"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "robot-learning",
    "title": "Robot learning",
    "group": "Autonomy, planning, and AI",
    "definition": "Improving robot behavior from demonstrations, trial and error, simulation, data, and feedback.",
    "coreIdeas": [
      "imitation learning",
      "reinforcement learning",
      "self supervised learning",
      "policies",
      "datasets"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "physical-ai",
    "title": "Physical AI",
    "group": "Autonomy, planning, and AI",
    "definition": "AI that acts through a physical body under contact, friction, gravity, safety limits, and sensor noise.",
    "coreIdeas": [
      "embodiment",
      "VLA models",
      "robot policies",
      "sim to real",
      "world models"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "vision-language-action-models",
    "title": "Vision language action models",
    "group": "Autonomy, planning, and AI",
    "definition": "Multimodal models that connect visual perception, language goals, and robot actions.",
    "coreIdeas": [
      "VLA",
      "language grounding",
      "action tokens",
      "robot data",
      "generalization"
    ],
    "relatedRobotTypes": [
      "mobile-manipulators",
      "humanoid-robots",
      "research-robots"
    ]
  },
  {
    "id": "sim-to-real-transfer",
    "title": "Sim to real transfer",
    "group": "Autonomy, planning, and AI",
    "definition": "Making behavior trained or tested in simulation survive on imperfect physical hardware.",
    "coreIdeas": [
      "domain randomization",
      "digital twins",
      "physics gaps",
      "hardware wear",
      "latency"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "teleoperation-and-shared-autonomy",
    "title": "Teleoperation and shared autonomy",
    "group": "Autonomy, planning, and AI",
    "definition": "Human control combined with robot assistance for data collection, hard tasks, and supervised deployment.",
    "coreIdeas": [
      "VR teleoperation",
      "haptics",
      "remote assistance",
      "intervention",
      "demonstrations"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "multi-robot-coordination",
    "title": "Multi robot coordination",
    "group": "Autonomy, planning, and AI",
    "definition": "Multiple robots sharing tasks, avoiding conflicts, communicating, and optimizing fleet performance.",
    "coreIdeas": [
      "fleet management",
      "task allocation",
      "traffic control",
      "swarm behavior",
      "coordination"
    ],
    "relatedRobotTypes": [
      "warehouse-robots-amrs",
      "agv-robots",
      "swarm-robotics"
    ]
  },
  {
    "id": "bipedal-locomotion",
    "title": "Bipedal locomotion",
    "group": "Movement and manipulation",
    "definition": "How two legged robots balance, walk, recover, and handle terrain under hardware limits.",
    "coreIdeas": [
      "gait",
      "balance",
      "ZMP",
      "MPC",
      "fall recovery"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "bipedal-research-robots"
    ]
  },
  {
    "id": "quadruped-locomotion",
    "title": "Quadruped locomotion",
    "group": "Movement and manipulation",
    "definition": "How four legged robots trot, climb, crawl, stabilize, and adapt to rough terrain.",
    "coreIdeas": [
      "terrain adaptation",
      "foot contact",
      "gait scheduling",
      "state estimation",
      "compliance"
    ],
    "relatedRobotTypes": [
      "robot-dogs-quadrupeds"
    ]
  },
  {
    "id": "wheeled-navigation",
    "title": "Wheeled navigation",
    "group": "Movement and manipulation",
    "definition": "Efficient ground mobility for AMRs, AGVs, service robots, and delivery robots.",
    "coreIdeas": [
      "odometry",
      "local planners",
      "map following",
      "traffic rules",
      "docking"
    ],
    "relatedRobotTypes": [
      "wheeled-mobile-robots",
      "warehouse-robots-amrs",
      "delivery-robots"
    ]
  },
  {
    "id": "aerial-robotics-control",
    "title": "Aerial robotics control",
    "group": "Movement and manipulation",
    "definition": "Flight stabilization, navigation, payload management, and safety for drones and aerial robots.",
    "coreIdeas": [
      "attitude control",
      "GNSS",
      "visual odometry",
      "geofencing",
      "payloads"
    ],
    "relatedRobotTypes": [
      "drones-aerial-robots",
      "inspection-drones",
      "multirotor-drones",
      "fixed-wing-drones",
      "vtol-hybrid-drones"
    ]
  },
  {
    "id": "underwater-robot-control",
    "title": "Underwater robot control",
    "group": "Movement and manipulation",
    "definition": "Thruster control, buoyancy, acoustic navigation, and mission autonomy below the surface.",
    "coreIdeas": [
      "thrusters",
      "sonar",
      "DVL",
      "pressure",
      "tethering"
    ],
    "relatedRobotTypes": [
      "underwater-robots",
      "autonomous-underwater-vehicles-auvs"
    ]
  },
  {
    "id": "manipulation",
    "title": "Manipulation",
    "group": "Movement and manipulation",
    "definition": "Moving objects with arms, hands, tools, suction, grippers, and contact rich strategies.",
    "coreIdeas": [
      "grasping",
      "dexterity",
      "force control",
      "tool use",
      "in hand manipulation"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "mobile-manipulators",
      "robotic-grippers-and-end-effectors"
    ]
  },
  {
    "id": "dexterous-hands",
    "title": "Dexterous hands",
    "group": "Movement and manipulation",
    "definition": "Multi finger hands with tactile sensing and complex control for tool use and manipulation.",
    "coreIdeas": [
      "tendons",
      "tactile arrays",
      "finger control",
      "underactuation",
      "slip"
    ],
    "relatedRobotTypes": [
      "humanoid-robot-hands",
      "humanoid-robots"
    ]
  },
  {
    "id": "soft-actuation",
    "title": "Soft actuation",
    "group": "Movement and manipulation",
    "definition": "Compliant actuators that bend, inflate, stretch, and deform for safe contact and delicate handling.",
    "coreIdeas": [
      "pneumatics",
      "elastomers",
      "strain sensors",
      "soft grippers",
      "bio inspiration"
    ],
    "relatedRobotTypes": [
      "soft-robotics",
      "robotic-grippers-and-end-effectors"
    ]
  },
  {
    "id": "robot-actuators",
    "title": "Robot actuators",
    "group": "Hardware, energy, and deployment",
    "definition": "Motors, hydraulics, pneumatics, tendons, transmissions, and mechanisms that convert energy into motion.",
    "coreIdeas": [
      "motors",
      "gearboxes",
      "hydraulics",
      "pneumatics",
      "series elastic actuators"
    ],
    "relatedRobotTypes": [
      "industrial-robots",
      "humanoid-robots",
      "soft-robotics"
    ]
  },
  {
    "id": "robot-batteries-and-power",
    "title": "Robot batteries and power",
    "group": "Hardware, energy, and deployment",
    "definition": "Energy storage, charging, thermal management, runtime, docking, and power distribution.",
    "coreIdeas": [
      "battery packs",
      "BMS",
      "charging docks",
      "hot swap",
      "thermal"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "embedded-computing",
    "title": "Embedded computing",
    "group": "Hardware, energy, and deployment",
    "definition": "Onboard CPUs, GPUs, microcontrollers, accelerators, networking, and real time systems.",
    "coreIdeas": [
      "edge AI",
      "real time",
      "CAN bus",
      "EtherCAT",
      "GPUs"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "ros-and-ros-2",
    "title": "ROS and ROS 2",
    "group": "Hardware, energy, and deployment",
    "definition": "Robotics middleware for communication, packages, visualization, simulation, control, and research workflows.",
    "coreIdeas": [
      "nodes",
      "topics",
      "services",
      "DDS",
      "robot packages"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "simulation-and-digital-twins",
    "title": "Simulation and digital twins",
    "group": "Hardware, energy, and deployment",
    "definition": "Virtual robot environments for testing control, planning, perception, safety, and deployment.",
    "coreIdeas": [
      "Gazebo",
      "Isaac Sim",
      "MuJoCo",
      "digital twins",
      "physics"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "fleet-management",
    "title": "Fleet management",
    "group": "Hardware, energy, and deployment",
    "definition": "Scheduling missions, assigning tasks, monitoring health, handling traffic, and coordinating many robots.",
    "coreIdeas": [
      "dispatch",
      "charging",
      "traffic",
      "telemetry",
      "uptime"
    ],
    "relatedRobotTypes": [
      "warehouse-robots-amrs",
      "agv-robots",
      "delivery-robots"
    ]
  },
  {
    "id": "maintenance-and-reliability",
    "title": "Maintenance and reliability",
    "group": "Hardware, energy, and deployment",
    "definition": "Keeping robots alive after launch through spare parts, diagnostics, calibration, logs, and service workflows.",
    "coreIdeas": [
      "MTBF",
      "intervention",
      "calibration",
      "wear",
      "logs"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "robotics-cybersecurity",
    "title": "Robotics cybersecurity",
    "group": "Hardware, energy, and deployment",
    "definition": "Protecting robot communication, fleet dashboards, remote control, updates, maps, and safety systems.",
    "coreIdeas": [
      "authentication",
      "updates",
      "network segmentation",
      "logs",
      "remote access"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "robotics-business-models",
    "title": "Robotics business models",
    "group": "Hardware, energy, and deployment",
    "definition": "Turning hardware, software, service, maintenance, data, and support into sustainable revenue.",
    "coreIdeas": [
      "RaaS",
      "leasing",
      "service contracts",
      "integration",
      "ROI"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "robotics-regulation-and-ethics",
    "title": "Robotics regulation and ethics",
    "group": "Hardware, energy, and deployment",
    "definition": "Standards, certification, labor impact, privacy, accountability, and responsible deployment.",
    "coreIdeas": [
      "standards",
      "privacy",
      "liability",
      "labor",
      "auditability"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  },
  {
    "id": "humanoid-robots-in-factories",
    "title": "Humanoid robots in factories",
    "group": "Robotics markets and applications",
    "definition": "From walking demos toward factory tasks such as tote handling, machine tending, and repetitive assistance.",
    "coreIdeas": [
      "humanoids",
      "manufacturing",
      "physical AI",
      "dexterity",
      "ROI"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "bipedal-research-robots"
    ]
  },
  {
    "id": "robot-dogs-in-inspection",
    "title": "Robot dogs in inspection",
    "group": "Robotics markets and applications",
    "definition": "Quadrupeds moving from viral videos into site mapping, industrial inspection, and hazardous observation.",
    "coreIdeas": [
      "quadrupeds",
      "LiDAR",
      "thermal",
      "inspection",
      "terrain"
    ],
    "relatedRobotTypes": [
      "robot-dogs-quadrupeds",
      "inspection-maintenance-robots"
    ]
  },
  {
    "id": "warehouse-automation",
    "title": "Warehouse automation",
    "group": "Robotics markets and applications",
    "definition": "AMRs, AGVs, picking robots, sorters, forklifts, and software for repetitive logistics work.",
    "coreIdeas": [
      "AMR",
      "AGV",
      "picking",
      "sortation",
      "fleet"
    ],
    "relatedRobotTypes": [
      "warehouse-robots-amrs",
      "agv-robots"
    ]
  },
  {
    "id": "medical-and-surgical-robotics",
    "title": "Medical and surgical robotics",
    "group": "Robotics markets and applications",
    "definition": "Precision machines and assistive systems for clinical workflows, rehabilitation, logistics, and procedures.",
    "coreIdeas": [
      "surgery",
      "rehabilitation",
      "hospital logistics",
      "safety",
      "workflow"
    ],
    "relatedRobotTypes": [
      "medical-surgical-robots",
      "rehabilitation-robots",
      "prosthetic-robotics",
      "exoskeletons",
      "hospital-logistics-robots"
    ]
  },
  {
    "id": "agriculture-robotics",
    "title": "Agriculture robotics",
    "group": "Robotics markets and applications",
    "definition": "Robots for crop scouting, weeding, harvesting, spraying, livestock monitoring, and farm labor gaps.",
    "coreIdeas": [
      "RTK",
      "crop perception",
      "weeding",
      "harvesting",
      "farm data"
    ],
    "relatedRobotTypes": [
      "agriculture-robots",
      "harvesting-robots",
      "weeding-robots",
      "milking-robots",
      "autonomous-tractors"
    ]
  },
  {
    "id": "drones-and-aerial-robotics",
    "title": "Drones and aerial robotics",
    "group": "Robotics markets and applications",
    "definition": "Flying robots for mapping, inspection, delivery, agriculture, research, media, and emergency support.",
    "coreIdeas": [
      "UAV",
      "flight control",
      "inspection",
      "mapping",
      "payloads"
    ],
    "relatedRobotTypes": [
      "drones-aerial-robots",
      "inspection-drones",
      "multirotor-drones",
      "fixed-wing-drones",
      "vtol-hybrid-drones"
    ]
  },
  {
    "id": "domestic-robotics",
    "title": "Domestic robotics",
    "group": "Robotics markets and applications",
    "definition": "Home robots where price, safety, noise, maintenance, privacy, and ease of use dominate performance.",
    "coreIdeas": [
      "cleaning",
      "companions",
      "home mapping",
      "privacy",
      "consumer design"
    ],
    "relatedRobotTypes": [
      "domestic-personal-robots",
      "cleaning-robots",
      "companion-robots"
    ]
  },
  {
    "id": "robotics-startups",
    "title": "Robotics startups",
    "group": "Robotics markets and applications",
    "definition": "Companies turning hard robot research into products, data loops, service contracts, and deployment playbooks.",
    "coreIdeas": [
      "funding",
      "RaaS",
      "hardware margins",
      "data engine",
      "market wedge"
    ],
    "relatedRobotTypes": [
      "humanoid-robots",
      "robot-dogs-quadrupeds",
      "warehouse-robots-amrs",
      "ai-powered-robotic-platforms"
    ]
  }
];
